import { AnimatePresence, motion } from "framer-motion";
import { AppLayout } from "../../../layout/AppLayout";
import {
  Crown,
  Live,
  Profile,
  Scoring,
  Time,
  Users,
} from "../../../components/svg/GeneralIcons";
import "react-multi-carousel/lib/styles.css";
import { FaLongArrowAltLeft, FaPlane } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useContext, useRef } from "react";
import { FullModal } from "../../../modules/world/rmc/modals/fullModal";
import clsx from "clsx";
import Button from "../../../components/world/rmc/Button";
import { useGetGameDetails } from "../../../api/getGameDetails";
import { UserContext } from "../../../components/context/UseContext";
import { useDeductFee } from "../../../api/deductFee";
import { Loader } from "../../../components/loader/Loader";
import { RmcContext } from "../../../components/context/RmcContext";

function WorldRules() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(UserContext);
  const searchParams = new URLSearchParams(location.search);
  const UrltriviaId = searchParams.get("trivia_id");
  const brandId = searchParams.get("id");
  const { getGameDetails, gameDetails, loading } = useGetGameDetails();
  const { setTriviaId, triviaId, slug } = useContext(RmcContext);
  const primaryColor = gameDetails?.brand?.secondary_color ?? "#FFD700";
  const iconBgColor = gameDetails?.brand?.primary_color ?? "#534D26";

  const howToPlay = [
    {
      icon: <Time color={primaryColor} bgColor={iconBgColor} />,
      title: "Time Limit",
      description: "Complete the challenge within the given time limit",
    },
    {
      icon: <Scoring color={primaryColor} bgColor={iconBgColor} />,
      title: "Scoring",
      description: "Earn points for each correct answer.",
    },
    {
      icon: <Users color={primaryColor} bgColor={iconBgColor} />,
      title: "Leaderboard",
      description: "Compete with other players on the leaderboard",
    },
  ];
  const {
    setFee,
    handelDeuctfee,
    isLoading: isDeducting,
    insufficient,
    setInsufficient,
    show,
    setShow,
  } = useDeductFee();

  const hasFetched = useRef(false);

  useEffect(() => {
    if (UrltriviaId && !hasFetched.current) {
      hasFetched.current = true;
      setTriviaId(UrltriviaId);
      getGameDetails(UrltriviaId);
    } else if (!UrltriviaId && !hasFetched.current) {
      hasFetched.current = true;
      getGameDetails(triviaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="h-[100dvh] bg-[#111522] flex items-center justify-center">
        <Loader type="spinningBubbles" color="#fff" />
      </div>
    );
  }

  return (
    <AppLayout
      bg={"!bg-[#111522]"}
      navBg={"!bg-[#111522]"}
      firstText={gameDetails?.brand?.name ?? "Kingdom Quest"}
      secondText={"I'm Live"}
      thirdText={"Achievements"}
      fourthText={"Profile"}
      firstIcon={
        gameDetails?.brand?.name?.toLowerCase().includes("airpeace") ? (
          <FaPlane className="w-5 h-5 -rotate-45" />
        ) : (
          <Crown className={"w-5 h-5"} />
        )
      }
      secondIcon={<Live className={"w-5 h-5"} />}
      fourthIcon={<Profile className={"w-5 h-5"} />}
      activeColor={gameDetails?.brand?.secondary_color ?? "#FFD700"}
      inactiveClass={"text-[#FFFFFFBF]"}
      firstLink={`${slug}/home`}
      secondLink={`${slug}/live`}
      thirdLink={`${slug}/achievements`}
      fourthLink={`${slug}/profile`}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 3, delay: 0.4 }}
        className="relative pt-10 pb-20"
      >
        <motion.div className="relative w-full flex px-5 items-center justify-center">
          <FaLongArrowAltLeft
            onClick={() => navigate(`/worlds/${slug}/home?id=${brandId}`)}
            className="text-lg text-white absolute left-5 cursor-pointer"
          />
          <p className="text-center text-xl text-white font-semibold">Trivia</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3, delay: 0.4 }}
          className="px-5"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 3, delay: 0.6 }}
            className="relative rounded-[10px] mt-10"
          >
            <img
              className="w-full object-contain rounded-[10px]"
              src={gameDetails?.brand?.image_url ?? "/image/knight.jpg"}
              alt="banner"
              loading="lazy"
            />
          </motion.div>
          {gameDetails === null && !loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mt-10 p-6 bg-gradient-to-r from-[#1A1F33] to-[#2A2F43] rounded-lg border-2"
              style={{ borderColor: primaryColor }}
            >
              <div className="text-center">
                <div className="mb-4">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="mx-auto"
                    style={{ color: primaryColor }}
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  This Week's Trivia is Wrapped Up! 🎉
                </h3>
                <p className="text-[#FFFFFF80] text-sm leading-relaxed mb-4">
                  Congratulations to all participants! This week's trivia has
                  come to an end. Our champions have been crowned and prizes
                  awarded.
                </p>
                <div className="bg-[#111522]/50 rounded-lg p-4 mb-4">
                  <p
                    className="text-sm font-medium mb-2"
                    style={{ color: primaryColor }}
                  >
                    📅 Next Quest Begins:
                  </p>
                  <p className="text-white font-semibold">
                    Monday - New Week, New Challenge!
                  </p>
                </div>
                <p className="text-[#FFFFFF80] text-xs">
                  Stay tuned for exciting new questions, bigger prizes, and more
                  opportunities to showcase your knowledge!
                </p>
              </div>
            </motion.div>
          ) : null}
          {gameDetails !== null && (
            <>
              {loading ? (
                <div className="mt-10">
                  <div className="h-8 bg-gray-600 rounded animate-pulse mb-2"></div>
                  <div className="h-4 bg-gray-600 rounded animate-pulse mb-1"></div>
                  <div className="h-4 bg-gray-600 rounded animate-pulse w-3/4"></div>
                </div>
              ) : (
                <>
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="tracking-tighter text-2xl font-semibold text-white mt-10"
                  >
                    {`${gameDetails?.name}` || "Kingdom Quest Trivia"}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="text-xs font-medium mt-2 text-[#FFFFFF80]"
                  >
                    {gameDetails?.data?.description ||
                      "Test your knowledge with our daily trivia challenge! Answer questions from various categories and complete for the top spot on the leaderboard and stand a chance to win amazing prizes."}
                  </motion.p>
                </>
              )}
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="tracking-tighter text-xl font-semibold mt-7 text-white mx-0"
              >
                How To Play
              </motion.h3>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="text-xs flex flex-col gap-3 font-medium mt-4 text-[#FFFFFF80]"
              >
                {howToPlay.map(({ title, description, icon }, i) => {
                  return (
                    <div className="flex items-center gap-2.5">
                      {icon}
                      <div className="flex flex-col gap-0.5">
                        <p className="text-base font-medium text-white">
                          {title}
                        </p>
                        <p className="text-xs font-medium text-[#FFFFFF80]">
                          {description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
              {loading ? (
                <div className="mt-10">
                  <div className="h-6 bg-gray-600 rounded animate-pulse mb-4"></div>
                  <div className="h-6 bg-gray-600 rounded animate-pulse mb-2"></div>
                  <div className="h-4 bg-gray-600 rounded animate-pulse mb-2"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-3 bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-3 bg-gray-600 rounded animate-pulse"></div>
                  </div>
                </div>
              ) : (
                <>
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="tracking-tighter text-xl font-semibold mt-10 text-white mx-0"
                  >
                    Entry Fee:{" "}
                    <span style={{ color: primaryColor }}>
                      ₦{gameDetails?.entry_fee || "200"}
                    </span>
                  </motion.h3>
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="tracking-tighter text-xl font-semibold mt-10 text-white mx-0"
                  >
                    Prize Pool
                  </motion.h3>
                  <p className="text-xs mt-4 font-medium text-[#FFFFFF80]">
                    The total prize pool for{" "}
                    {`${gameDetails?.name} Quest` || "Kingdom Quest"} includes
                    prizes as follows:
                  </p>
                  <ul className="list-disc px-4">
                    {gameDetails?.game?.prizes?.length > 0 ? (
                      gameDetails?.game?.prizes.map((prize, index) => (
                        <li
                          key={index}
                          className="text-[#FFFFFF80] mt-0.5 text-xs font-medium"
                        >
                          {prize.name || prize}
                        </li>
                      ))
                    ) : (
                      <>
                        <li className="text-[#FFFFFF80] mt-0.5 text-xs font-medium">
                          Headphones
                        </li>
                        <li className="text-[#FFFFFF80] mt-0.5 text-xs font-medium">
                          Iphone 16
                        </li>
                        <li className="text-[#FFFFFF80] mt-0.5 text-xs font-medium">
                          Paid Expense Trip
                        </li>
                        <li className="text-[#FFFFFF80] mt-0.5 text-xs font-medium">
                          Iphone 16
                        </li>
                      </>
                    )}
                  </ul>
                </>
              )}
              <p className="text-xs  mt-4 font-medium text-[#FFFFFF80]">
                The more you play to more you stand to win!
              </p>
              <button
                onClick={() => {
                  setFee(gameDetails?.entry_fee);
                  setShow(true);
                }}
                className="mt-14 h-[60px] w-full font-semibold text-lg rounded-[10px] flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                Play Challenge
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
      <AnimatePresence>
        {show && (
          <FullModal
            className={clsx(
              "h-[478px] fixed inset-x-0 bottom-0 z-[4000] bg-[#111522] animate-slide-up 480:max-w-[440px] 480:mx-auto rounded-t-[20px]",
            )}
            onClose={() => setShow(false)}
            showCloseIcon
          >
            <div className="px-5 pt-16 flex flex-col items-center">
              <Wallet color={primaryColor} />
              <p className="my-2 text-center text-xl font-bold text-white">
                Confirm Entry Fee
              </p>
              <p className="font-medium text-center text-[#FFFFFF80]">
                You are about to be charged
              </p>
              <div className="mt-7">
                <p className="font-bold text-3xl text-center" style={{ color: primaryColor }}>
                  ₦{gameDetails?.entry_fee || 0}
                </p>
                <p className="font-medium text-[#FFFFFF80] text-center">
                  from your main wallet
                </p>
              </div>
              <div className="w-full flex flex-col gap-5 mt-10">
                <Button
                  onClick={() => handelDeuctfee(brandId, gameDetails?.id)}
                  text={
                    isDeducting ? <Loader type="bubbles" /> : "Proceed to Game"
                  }
                  className={isDeducting ? "!py-1" : "text-lg py-[18px]"}
                  style={{ backgroundColor: primaryColor }}
                />
                <Button
                  onClick={() => setShow(false)}
                  text="Cancel"
                  className="text-lg py-[18px] !bg-[#1A1F33] !text-[#FFFFFFBF]"
                />
              </div>
            </div>
          </FullModal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {insufficient && (
          <FullModal
            className={clsx(
              "h-[432px] fixed inset-x-0 bottom-0 z-[4000] bg-[#111522] animate-slide-up 480:max-w-[440px] 480:mx-auto rounded-t-[20px]",
            )}
            onClose={() => setInsufficient(false)}
            showCloseIcon
          >
            <div className="px-5 pt-16 flex flex-col items-center">
              <InsufficientWallet />
              <p className="my-2 text-center text-xl font-bold text-white">
                Insufficient Funds
              </p>
              <p className="font-medium text-center text-[#FFFFFF80]">
                Oh no! It looks like your wallet is a bit light. Please add
                funds to continue your Arena XP journey.
              </p>

              <div className="w-full flex flex-col gap-5 mt-10">
                <Button
                  onClick={() =>
                    navigate(`/worlds/${slug}/achievements?tab=wallet`)
                  }
                  text={
                    <div className="flex justify-center items-center gap-2.5">
                      <WalletPlus />
                      <p className="text-white text-base font-medium ">
                        Fund Wallet
                      </p>
                    </div>
                  }
                  className={"text-lg py-[18px] !bg-[#E92932]"}
                />
                <Button
                  onClick={() => setInsufficient(false)}
                  text="Cancel"
                  className="text-lg py-[18px] !bg-[#1A1F33] !text-[#FFFFFFBF]"
                />
              </div>
            </div>
          </FullModal>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}

export default WorldRules;

const Wallet = ({ color = "#FFD700" }) => {
  return (
    <svg
      width="59"
      height="58"
      viewBox="0 0 59 58"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" width="58" height="58" rx="29" fill={color} />
      <path
        d="M37 25V21.5C37 21.1022 36.842 20.7206 36.5607 20.4393C36.2794 20.158 35.8978 20 35.5 20H22C21.6022 20 21.2206 20.158 20.9393 20.4393C20.658 20.7206 20.5 21.1022 20.5 21.5V36.5C20.5 36.8978 20.658 37.2794 20.9393 37.5607C21.2206 37.842 21.6022 38 22 38H35.5C35.8978 38 36.2794 37.842 36.5607 37.5607C36.842 37.2794 37 36.8978 37 36.5V33"
        stroke="black"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M37 25H30C29.1716 25 28.5 25.6716 28.5 26.5V31.5C28.5 32.3284 29.1716 33 30 33H37C37.8284 33 38.5 32.3284 38.5 31.5V26.5C38.5 25.6716 37.8284 25 37 25Z"
        stroke="black"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M31.5 29C31.5 28.4477 31.9477 28 32.5 28C33.0523 28 33.5 28.4477 33.5 29C33.5 29.5523 33.0523 30 32.5 30C31.9477 30 31.5 29.5523 31.5 29Z"
        fill="black"
      />
    </svg>
  );
};

const InsufficientWallet = () => {
  return (
    <svg
      width="73"
      height="57"
      viewBox="0 0 73 57"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M49.8326 13.8889V7.66667C49.8326 6.95942 49.5517 6.28115 49.0516 5.78105C48.5515 5.28095 47.8733 5 47.166 5H23.1666C22.4594 5 21.7811 5.28095 21.281 5.78105C20.7809 6.28115 20.5 6.95942 20.5 7.66667V34.3333C20.5 35.0406 20.7809 35.7189 21.281 36.219C21.7811 36.719 22.4594 37 23.1666 37H47.166C47.8733 37 48.5515 36.719 49.0516 36.219C49.5517 35.7189 49.8326 35.0406 49.8326 34.3333V28.1111"
        stroke="#E92932"
        stroke-width="3"
        stroke-linejoin="round"
      />
      <path
        d="M49.8334 13.8892H37.3893C35.9165 13.8892 34.7227 15.0831 34.7227 16.5558V25.4447C34.7227 26.9175 35.9165 28.1114 37.3893 28.1114H49.8334C51.3061 28.1114 52.5 26.9175 52.5 25.4447V16.5558C52.5 15.0831 51.3061 13.8892 49.8334 13.8892Z"
        stroke="#E92932"
        stroke-width="3"
        stroke-linejoin="round"
      />
      <path
        d="M42.1094 21C42.1094 20.8467 41.9852 20.7219 41.832 20.7217C41.6787 20.7217 41.5537 20.8466 41.5537 21C41.5539 21.1532 41.6788 21.2773 41.832 21.2773C41.9851 21.2771 42.1091 21.1531 42.1094 21Z"
        fill="#E92932"
        stroke="#E92932"
        stroke-width="3"
      />
    </svg>
  );
};

const WalletPlus = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H14V18H4V12H22V6C22 4.89 21.11 4 20 4ZM20 8H4V6H20V8ZM24 17V19H21V22H19V19H16V17H19V14H21V17H24Z"
        fill="white"
      />
    </svg>
  );
};
